import { useQuery, useQueryClient } from "@tanstack/react-query";
import queryString from "query-string";
import * as React from "react";
import ApiClient from "../../api";
import StudentTag from "../../domains/StudentTag";
import { createError } from "../../errors";

type Props = {
  sectionId: string;
  query?: Record<string, unknown>;
  enabled?: boolean;
  activeStudent?: boolean;
};
type ResponseBody = {
  tags: StudentTag[];
  query: string;
};
// APIからタグ全件欲しいときは十分に大きい数字をperに入れて投げるという仕様
const MAX_PER = 1000000000;

export const useFetchStudentTags = ({
  sectionId,
  query = {},
  enabled = true,
  activeStudent = true,
}: Props) => {
  const params = { ...query, per: MAX_PER, active_student: activeStudent };
  const queryParams = `?${queryString.stringify(params)}`;

  return useQuery<ResponseBody, Error>({
    queryKey: cacheKeyOf(sectionId, queryParams),
    queryFn: async () => {
      const response = await ApiClient.get(
        `/api/v1/sections/${sectionId}/all_tags`,
        { query: queryParams },
      );
      if (response.ok) {
        const json = await response.json();
        return {
          tags: json.allTags.data as StudentTag[],
          query: queryParams,
        };
      }
      throw await createError(response);
    },
    enabled,
  });
};

export const useUpdateStudentTagsCache = (sectionId: string) => {
  const queryClient = useQueryClient();
  const queryKey = cacheKeyOf(sectionId);

  const createTag = React.useCallback(
    (targetTag: StudentTag) => {
      queryClient.setQueriesData<ResponseBody>({ queryKey }, (oldData) => {
        if (!oldData) {
          return oldData;
        }
        // 新規タグは先頭に追加
        return {
          ...oldData,
          tags: [targetTag, ...oldData.tags],
        };
      });
    },
    [queryClient, queryKey],
  );

  const updateTag = React.useCallback(
    (targetTag: StudentTag) => {
      queryClient.setQueriesData<ResponseBody>({ queryKey }, (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          tags: oldData.tags.map((tag) =>
            tag.id === targetTag.id ? targetTag : tag,
          ),
        };
      });
    },
    [queryClient, queryKey],
  );

  const deleteTag = React.useCallback(
    (targetTag: StudentTag) => {
      queryClient.setQueriesData<ResponseBody>({ queryKey }, (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          tags: oldData.tags.filter((tag) => tag.id !== targetTag.id),
        };
      });
    },
    [queryClient, queryKey],
  );

  return {
    createTag,
    updateTag,
    deleteTag,
  };
};

const cacheKeyOf = (sectionId: string, queryParams?: string) =>
  queryParams
    ? ["student_tags", sectionId, queryParams]
    : ["student_tags", sectionId];
